'use client';
/**
 * The lazy shell: the calculator (engine, stage, deck, readouts) is a separate chunk (next/dynamic, ssr:false) that mounts into a
 * server-rendered skeleton of the same size, so the H1 and the prose stay in the server HTML and the page does not shift.
 * This skeleton (shimmering shapes of the real frame, plus the real default title and hero number as TEXT so the first large paint on a phone comes from the server HTML, not from the lazy chunk: the redesign's mobile LCP was 9 s without it; the text is aria-hidden, the status is the screen-reader label) and the mounted calculator are both display:contents wrappers whose children sit in the app frame's grid areas
 * (side, top, canvas), so the frame is exactly the same size before and after mount. A failed chunk shows the live fallback ("Calculator Update Required", "Refresh Page").
 */
import dynamic from 'next/dynamic';
import { Component } from 'react';
import type { ReactNode } from 'react';
import type { RoiGameRootProps } from './RoiGameRoot';

const Rows = ({ n }: { n: number }) => (
  <>{Array.from({ length: n }, (_, i) => (
    <div key={i}><div className="sk-row"><span className="sk" /><span className="sk" /></div><span className="sk sk-rng" /></div>
  ))}</>
);

/** The real app frame as shimmering shapes (sidebar, tab strip, hero card, five tiles), the same boxes the mounted calculator fills. */
export function RoiSkeleton() {
  return (
    <div className="rga-root rga-skel" id="roi-run" aria-busy="true">
      <div role="status" className="rga-sr">Loading the calculator</div>
      <aside className="rga-side sk-side" aria-hidden="true">
        <span className="sk sk-t" />
        {[0, 1, 2].map((i) => (
          <div className="sk-f" key={i}><span className="sk sk--soft sk-cap" /><div className="sk-seg"><span className="sk" /><span className="sk" />{i === 1 ? <span className="sk" /> : null}</div></div>
        ))}
        <span className="sk sk-g sk--open" />
        <div className="sk-open"><Rows n={3} /></div>
        {[0, 1, 2, 3].map((i) => <span key={i} className="sk sk--soft sk-g" />)}
      </aside>
      <div className="rga-top sk-top" aria-hidden="true"><span className="sk" /><span className="sk" /><span className="sk" /></div>
      <div className="rga-canvas" aria-hidden="true">
        <div className="sk-rr">
          <div className="sk-head"><p className="sk-title">Paid Ads Performance</p><span className="sk" /></div>
          <div className="sk-tabs"><span className="sk" /><span className="sk" /><span className="sk" /></div>
          <div className="sk-stack">
            <div className="sk sk-hero"><div><b className="sk-lab">Cost per funded loan</b><i className="sk-num">$1,667</i></div></div>
            {[0, 1, 2, 3, 4].map((i) => <span key={i} className="sk sk-tile" />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChunkError() {
  return (
    <div className="rg-err" role="alert">
      <h2>Calculator Update Required</h2>
      <p>The calculator has been updated. Please refresh your browser to load the latest version.</p>
      <button type="button" onClick={() => window.location.reload()}>Refresh Page</button>
    </div>
  );
}

const Root = dynamic<RoiGameRootProps>(
  () => import('./RoiGameRoot').catch((err) => { console.error('Failed to load the ROI calculator chunk:', err); return { default: ChunkError as never }; }),
  { ssr: false, loading: () => <RoiSkeleton /> },
);

class Boundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() { return this.state.failed ? <ChunkError /> : this.props.children; }
}

export function RoiGameLazy(props: RoiGameRootProps) {
  return <Boundary><Root {...props} /></Boundary>;
}
